import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, Phone, Mail, Calendar, MoreVertical, Eye, Edit, BarChart3, TrendingUp } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import StudentStatusToggle from '../StudentStatusToggle';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  belt_level_id?: string;
  join_date?: string;
  created_at?: string;
  dojos?: { name: string };
  is_active?: boolean;
}

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
}

interface StudentCardProps {
  student: Student;
  onDelete: (studentId: string, studentName: string) => Promise<void>;
  onStatusChange?: () => void;
  children?: React.ReactNode;
}

const StudentCard: React.FC<StudentCardProps> = ({ student, onDelete, onStatusChange }) => {
  useUiLanguage();
  const { t } = useTranslation('common');
  const [attendanceData, setAttendanceData] = useState<{ rate: number; attended: number; total: number } | null>(null);
  const [beltLevel, setBeltLevel] = useState<BeltLevel | null>(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: student.first_name,
    last_name: student.last_name,
    email: student.email,
    phone: student.phone || ''
  });
  const [attendanceHistory, setAttendanceHistory] = useState<any[]>([]);
  const [confirmedStatus, setConfirmedStatus] = useState(student.is_active);
  useEffect(() => { setConfirmedStatus(student.is_active); }, [student.id, student.is_active]);

  useEffect(() => {
    fetchAttendanceData();
    if (student.belt_level_id) {
      fetchBeltLevel();
    }
  }, [student.id, student.belt_level_id]);

  const fetchAttendanceData = async () => {
    try {
      const { data, error } = await supabase
        .from('session_attendance')
        .select('attended')
        .eq('student_id', student.id);

      if (error) {
        console.error('Error fetching attendance:', error);
        return;
      }

      const total = data?.length || 0;
      const attended = data?.filter(record => record.attended).length || 0;
      const rate = total > 0 ? Math.round((attended / total) * 100) : 0;

      setAttendanceData({ rate, attended, total });
    } catch (error) {
      console.error('Error processing attendance:', error);
    }
  };

  const fetchBeltLevel = async () => {
    if (!student.belt_level_id) return;

    try {
      const { data, error } = await supabase
        .from('belt_levels')
        .select('*')
        .eq('id', student.belt_level_id)
        .single();

      if (error) {
        console.error('Error fetching belt level:', error);
        return;
      }

      setBeltLevel(data);
    } catch (error) {
      console.error('Error fetching belt level:', error);
    }
  };

  const getBeltColorClass = (color?: string) => {
    if (!color) return 'bg-gray-500 text-white';

    const colorMap: { [key: string]: string } = {
      white: 'bg-gray-100 text-gray-900 border border-gray-300',
      orange: 'bg-orange-500 text-white',
      blue: 'bg-blue-500 text-white',
      yellow: 'bg-yellow-400 text-black',
      green: 'bg-green-500 text-white',
      purple: 'bg-purple-500 text-white',
      brown: 'bg-amber-700 text-white',
      black: 'bg-black text-white'
    };
    return colorMap[color] || 'bg-gray-500 text-white';
  };

  const handleDelete = () => {
    onDelete(student.id, `${student.first_name} ${student.last_name}`);
  };

  const handleViewProfile = () => {
    setShowProfileDialog(true);
  };

  const handleEditDetails = () => {
    setShowEditDialog(true);
  };

  const handleViewAttendance = async () => {
    try {
      const { data, error } = await supabase
        .from('session_attendance')
        .select(`
          *,
          training_sessions (
            title,
            date,
            start_time
          )
        `)
        .eq('student_id', student.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching attendance history:', error);
        toast({
          title: t("common:messages.error"),
          description: uiText("Failed to load attendance history"),
          variant: "destructive"
        });
        return;
      }

      setAttendanceHistory(data || []);
      setShowAttendanceDialog(true);
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const handleUpdateStudent = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: editForm.first_name,
          last_name: editForm.last_name,
          email: editForm.email,
          phone: editForm.phone
        })
        .eq('id', student.id);

      if (error) {
        console.error('Error updating student:', error);
        toast({
          title: t("common:messages.error"),
          description: uiText("Failed to update student details"),
          variant: "destructive"
        });
        return;
      }

      toast({
        title: t("common:messages.success"),
        description: uiText("Student details updated successfully")
      });

      setShowEditDialog(false);
      // Refresh the page to see updated data
      window.location.reload();
    } catch (error) {
      console.error('Error updating student:', error);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return uiText("N/A");
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Card className="wh-student-record">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
          {/* Student Info */}
          <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
            <div className="wh-icon-tile">
              <span className="text-sm font-semibold">{student.first_name?.[0]}{student.last_name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-base sm:text-lg">
                  {student.first_name} {student.last_name}
                </h4>
                {student.dojos?.name && (
                  <Badge variant="outline" className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border-blue-200 mt-1 sm:mt-0 self-start">
                    {student.dojos.name}
                  </Badge>
                )}
              </div>

              {/* Contact Info Grid */}
              <div className="grid grid-cols-1 gap-2 sm:gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium truncate text-xs sm:text-sm">{student.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate text-xs sm:text-sm">{student.phone || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-xs sm:text-sm">{uiText("Joined")}</span>
                  <span className="font-medium text-xs sm:text-sm">{formatDate(student.created_at)}</span>
                </div>
              </div>

              {/* Attendance Progress */}
              <div className="space-y-2 pt-3 sm:pt-4 border-t">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                  <span className="text-xs sm:text-sm text-muted-foreground">{uiText("Attendance Rate")}</span>
                  <span className="font-medium text-xs sm:text-sm">{attendanceData?.rate || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                    style={{ width: `${attendanceData?.rate || 0}%` }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {attendanceData?.attended || 0} {' '}{uiText("attended of")}{' '}{attendanceData?.total || 0} {' '}{uiText("sessions")}</p>
              </div>
            </div>
          </div>

          {/* Status and Actions - Mobile Optimized */}
          <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start sm:space-y-4 space-x-2 sm:space-x-0 flex-shrink-0">
            <div className="flex flex-col sm:flex-col items-start sm:items-end space-y-2">
              {/* Status Badge */}
              <StudentStatusToggle studentId={student.id} currentStatus={confirmedStatus} onStatusChange={active => { setConfirmedStatus(active); onStatusChange?.(); }} />

              {/* Current Belt */}
              <div className="text-left sm:text-right">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("common:students.currentBelt")}</p>
                <Badge
                  className={`text-xs sm:text-sm font-medium ${getBeltColorClass(beltLevel?.color)}`}
                  title={beltLevel?.name || t("common:students.noBeltAssigned")}
                >
                  {beltLevel?.name || t("common:students.noBeltAssigned")}
                </Badge>
              </div>
            </div>

            {/* Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50">
                <DropdownMenuItem
                  className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={handleViewProfile}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t("common:students.viewProfile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={handleEditDetails}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t("common:students.editDetails")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={handleViewAttendance}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  {t("common:students.viewAttendance")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer"
                  onClick={handleDelete}
                >
                  {t("common:students.deleteStudent")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t("common:students.studentProfile")}</DialogTitle>
            <DialogDescription>
              {t("common:students.detailedInformation")} {student.first_name} {student.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">{t("common:students.firstName")}</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">{student.first_name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">{t("common:students.lastName")}</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">{student.last_name}</p>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">{t("common:labels.email")}</Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">{student.email}</p>
            </div>
            {student.phone && (
              <div>
                <Label className="text-sm font-medium">{t("common:labels.phone")}</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">{student.phone}</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">{t("common:students.currentBelt")}</Label>
              <div className="mt-1">
                <Badge className={`${getBeltColorClass(beltLevel?.color)}`}>
                  {beltLevel?.name || t("common:students.noBeltAssigned")}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">{t("common:students.joinDate")}</Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {student.join_date ? new Date(student.join_date).toLocaleDateString(currentLocale()) : 'N/A'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">{t("common:students.attendanceRate")}</Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {attendanceData?.rate || 0}% ({attendanceData?.attended || 0}/{attendanceData?.total || 0} {' '}{uiText("sessions)")}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("common:students.editStudentDetails")}</DialogTitle>
            <DialogDescription>
              {t("common:students.updateInformation")} {student.first_name} {student.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">{t("common:students.firstName")}</Label>
                <Input
                  id="first_name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t("common:students.lastName")}</Label>
                <Input
                  id="last_name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">{t("common:labels.email")}</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="phone">{t("common:labels.phone")}</Label>
              <Input
                id="phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                {t("common:buttons.cancel")}
              </Button>
              <Button onClick={handleUpdateStudent}>
                {t("common:students.updateStudent")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("common:students.attendanceHistory")}</DialogTitle>
            <DialogDescription>
              {t("common:students.attendanceRecords")} {student.first_name} {student.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {attendanceData && (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h3 className="font-medium mb-2">{t("common:students.overallStatistics")}</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">{t("common:students.attendanceRate")}</p>
                    <p className="font-medium text-lg">{attendanceData.rate}%</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">{t("common:students.sessionsAttended")}</p>
                    <p className="font-medium text-lg">{attendanceData.attended}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">{t("common:students.totalSessions")}</p>
                    <p className="font-medium text-lg">{attendanceData.total}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <h3 className="font-medium">{t("common:students.recentSessions")}</h3>
              {attendanceHistory.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t("common:students.noAttendanceRecords")}</p>
              ) : (
                attendanceHistory.map((record, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border rounded-lg">
                    <div>
                      <p className="font-medium">{record.training_sessions?.title || t("common:students.trainingSession")}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {record.training_sessions?.date ? new Date(record.training_sessions.date).toLocaleDateString(currentLocale()) : t("common:students.dateNotAvailable")}
                        {record.training_sessions?.start_time && uiText(" at {{value0}}", { value0: record.training_sessions.start_time })}
                      </p>
                    </div>
                    <Badge variant={record.attended ? "default" : "secondary"}>
                      {record.attended ? t("common:students.attended") : t("common:students.missed")}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StudentCard;
